import { useEffect } from 'react';
import { useAppContext } from '../../Context';
import { AttributesType, ColumnsType, OrderType } from '../../types';

import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/shared/DataTable';

// hooks redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks';
import { setOrder, setOrdersInProcess } from '../../slices/ordersSlice';
import { setLoading, setPopConfirmation } from '../../slices/miscSlice';

import { printOrderTable, usePermissions } from '../../services';
import { Grid } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash, faPrint, faPlay, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { constants } from '../../constants';
import React from 'react';

const ViewOrdersProcessed: React.FC = () => {
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ordersInProcess = useSelector((state: any) => state.orders.ordersInProcess);

  const {
    canView_orders,
    canView_orders_all,
    canView_orders_own,
    canView_menu_orders_in_process,
    canDelete_orders,
    canAdd_process,
  } = usePermissions([
    'view_orders',
    'view_orders_all',
    'view_orders_own',
    'view_menu_orders_in_process',
    'delete_orders',
    'add_process',
  ]);

  const getOrders = async () => {
    const response = await apiCall('GET', 'orders/processed/', null, '', 'Error al obtener las ordenes');
    if (response) {
      dispatch(setOrdersInProcess(response));
    }
  };

  useEffect(
    () => {
      getOrders();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!(canView_orders || canView_orders_all || canView_orders_own || canView_menu_orders_in_process)) {
    navigate('/');
    return null;
  }

  const hoyOnlyDate = new Date();
  hoyOnlyDate.setHours(0, 0, 0, 0);

  const conditionDecorator = {
    operator: '<',
    value: hoyOnlyDate,
    class_decorator: 'text-danger',
    type: 'date',
    limit: constants.settings.limitDateDays,
  };

  const decoratorEstado = {
    Finalizado: 'success',
    Bloqueado: 'warning',
    Creada: 'info',
    Cancelada: 'error',
  };

  const processStatusDecorator = {
    'En Proceso': 'secondary',
    'Finalizado': 'success',
    'Bloqueado': 'warning',
    'Por Iniciar': 'info',
    'Cancelado': 'error',
    'Cons en prod': 'secondary',
  };

  const ordersInProcessWithStatus = ordersInProcess.map((order: OrderType) => {
    const currentProcessStatusDisplay = order.current_process_status || 'Cons en prod';
    const canSendToProduction = currentProcessStatusDisplay === 'Cons en prod';
    return {
      ...order,
      current_process_status_display: currentProcessStatusDisplay,
      can_send_to_production: canSendToProduction,
      can_send_order_only: canSendToProduction && (order.consecutives_count ?? 0) > 0,
    };
  });

  const columns: Array<ColumnsType> = [
    { title: 'ID', field: 'id' },
    { title: 'Cliente', field: 'customer_name' },
    { title: 'Línea', field: 'line_name' },
    { title: 'Cons', field: 'consecutives_count', cell_class: 'text-center' },
    { title: 'Vendedor', field: 'vendedor', cell_class: 'text-center' },
    { title: 'Fecha', field: 'order_date', cell_class: 'text-center' },
    { title: 'Inicio Prod.', field: 'production_date_start', cell_class: 'text-center' },
    {
      title: 'Fin Prod.',
      field: 'production_date_end',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Entrega',
      field: 'delivery_date',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    { title: 'Proceso¹', field: 'current_process_name', cell_class: 'text-center' },
    {
      title: 'Estado Proceso',
      field: 'current_process_status_display',
      type: 'decorator',
      decorator: processStatusDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Estado Orden',
      field: 'status',
      type: 'decorator',
      decorator: decoratorEstado,
      cell_class: 'text-center',
    },
  ];

  const handleViewOrder = (order: OrderType) => {
    dispatch(setOrder(order));
    navigate(`/orden/${order.id}`);
  };

  const handleDeleteOrder = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea eliminar la orden?',
        alertType: 'danger',
        okFunc: () => deleteOrder(order.id),
      })
    );
  };
  const deleteOrder = async (order_id: number) => {
    const response = await apiCall('DELETE', `order/${order_id}/`, null, '', 'Error al eliminar la orden');
    if (response) {
      dispatch(setOrdersInProcess(ordersInProcess.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const handlePrintOrder = async (order: OrderType) => {
    let boceto = null;
    const resp = await apiCall('GET', `order/${order.id}/boceto/`, null, '', 'Error al obtener la imagen');
    if (resp?.image) {
      boceto = resp.image;
    }
    dispatch(setLoading(true));
    await printOrderTable(order, boceto);
    dispatch(setLoading(false));
  };

  const handleAddProcess = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea enviar la orden a producción?',
        alertType: 'danger',
        okFunc: () => addProcess(order.id),
      })
    );
  };

  const addProcess = async (order_id: number) => {
    const response = await apiCall(
      'POST',
      `order/${order_id}/add_process/`,
      null,
      '',
      'Error al enviar la orden a producción'
    );
    if (response) {
      await getOrders();
    }
  };

  const handleAddProcessOrderOnly = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea enviar la orden a producción sin consecutivos?',
        alertType: 'danger',
        okFunc: () => addProcessOrderOnly(order.id),
      })
    );
  };

  const addProcessOrderOnly = async (order_id: number) => {
    const response = await apiCall(
      'POST',
      `order/${order_id}/add_process/`,
      { only_order: true },
      '',
      'Error al enviar la orden a producción'
    );
    if (response) {
      await getOrders();
    }
  };

  const canViewOrderDetail =
    canView_orders || canView_orders_all || canView_orders_own || canView_menu_orders_in_process;

  const actions: Array<any> = [
    canViewOrderDetail
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: 'Ver',
          onClick: (rowData: OrderType) => handleViewOrder(rowData),
        }
      : null,
    canDelete_orders
      ? {
          icon: <FontAwesomeIcon icon={faTrash} />,
          tooltip: 'Eliminar',
          onClick: (rowData: any) => handleDeleteOrder(rowData),
        }
      : null,
    canView_orders
      ? {
          icon: <FontAwesomeIcon icon={faPrint} />,
          tooltip: 'Imprimir',
          onClick: (rowData: any) => handlePrintOrder(rowData),
        }
      : null,
    canAdd_process
      ? {
          icon: <FontAwesomeIcon icon={faPlay} />,
          tooltip: 'Enviar a producción',
          onClick: (rowData: OrderType) => handleAddProcess(rowData),
          condition: { field: 'can_send_to_production', value: true, operator: '===' },
        }
      : null,
    canAdd_process
      ? {
          icon: <FontAwesomeIcon icon={faPaperPlane} />,
          tooltip: 'Enviar solo orden a producción(no consecutivos)',
          onClick: (rowData: OrderType) => handleAddProcessOrderOnly(rowData),
          condition: { field: 'can_send_order_only', value: true, operator: '===' },
        }
      : null,
  ];

  const filterDate: Array<AttributesType> = [
    { value: 'order_date', name: 'Fecha de Creación' },
    { value: 'production_date_start', name: 'Fecha de Inicio de Produccion' },
    { value: 'production_date_end', name: 'Fecha de Fin de Produccion' },
    { value: 'delivery_date', name: 'Fecha de Entrega' },
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>Órdenes En Proceso</h2>
        </Grid>
      </Grid>
      <DataTable data={ordersInProcessWithStatus} columns={columns} actions={actions} filterByDateBy={filterDate} />
      <small>¹ Indica el proceso individual de la orden pero no necesariamente el de sus consecutivos en caso de que tenga.</small>
    </div>
  );
};

export default ViewOrdersProcessed;
